import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useMobileNavigate } from '../../mobile/MobileNavigationGuard';
import { Card, CardContent, CardHeader } from '../../ui/card';
import { ScrollArea } from '../../ui/scroll-area';
import { Button } from '../../ui/button';
import { ConversationList } from '../conversations/ConversationList';
import { ConversationThread } from '../thread/ConversationThread';
import { NewConversationDialog } from '../dialogs/NewConversationDialog';
import { useMessagesContext } from '../../../context/MessagesContext';
import { useAuth } from '../../../context/AuthContext';
import { Loader2, MessageCircle, MessageSquarePlus, RefreshCw, ExternalLink } from 'lucide-react';
import { useModalState } from '../../../hooks/useModalState';
import { EmptyState } from '../../ui/EmptyState';
import { getUserDisplayName } from '../../userMenu/utils';

export function MessagesInbox() {
  const navigate = useMobileNavigate();
  const { user } = useAuth();
  const { conversations, loading, error, fetchConversations, requestFullList, releaseFullList } = useMessagesContext();
  
  // Use refs to avoid re-running effect when callbacks recreate
  const requestFullListRef = useRef(requestFullList);
  const releaseFullListRef = useRef(releaseFullList);
  useEffect(() => {
    requestFullListRef.current = requestFullList;
    releaseFullListRef.current = releaseFullList;
  }, [requestFullList, releaseFullList]);
  
  // Request full list when component mounts, release when unmounts
  useEffect(() => {
    requestFullListRef.current();
    return () => {
      releaseFullListRef.current();
    };
  }, []);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const {
    isOpen: newConversationOpen,
    open: openNewConversation,
    setOpen: setNewConversationOpen,
  } = useModalState();

  const handleRefresh = React.useCallback(() => {
    fetchConversations();
  }, [fetchConversations]);

  const handleConversationClick = React.useCallback((conversationId: string) => {
    setSelectedConversationId(conversationId);
  }, []);

  const handleBackToInbox = React.useCallback(() => {
    setSelectedConversationId(null);
  }, []);

  // Get current user's display name using shared utility
  const currentUserName = useMemo(() => getUserDisplayName(user) || null, [user]);

  return (
    <>
      <Card className="border-none shadow-none">
        {!selectedConversationId && (
          <CardHeader className="flex flex-row items-center justify-between space-y-0 px-2 py-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => navigate('/messages', { state: { from: { label: 'Messages Inbox', href: undefined } } })}
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              Open all messages
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={openNewConversation}
                aria-label="New conversation"
              >
                <MessageSquarePlus className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleRefresh} disabled={loading} aria-label="Refresh messages">
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </div>
          </CardHeader>
        )}
        <CardContent className="p-0">
          {selectedConversationId ? (
            <ConversationThread conversationId={selectedConversationId} onBack={handleBackToInbox} />
          ) : (
            <>
              {loading && (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              )}
              {error && (
                <div className="p-4 text-sm text-destructive">
                  <p>Error loading messages: {error}</p>
                </div>
              )}
              {!loading && conversations.length === 0 && (
                <div className="p-4">
                  <EmptyState
                    size="compact"
                    variant="ghost"
                    icon={<MessageCircle className="h-5 w-5" aria-hidden="true" />}
                    description="No conversations yet."
                    actions={
                      <Button variant="outline" size="sm" onClick={openNewConversation}>
                        <MessageSquarePlus className="mr-2 h-4 w-4" />
                        Start New Conversation
                      </Button>
                    }
                  />
                </div>
              )}
              {!loading && conversations.length > 0 && (
                <ScrollArea className="h-[400px]">
                  <ConversationList conversations={conversations} onConversationClick={handleConversationClick} currentUserName={currentUserName} />
                </ScrollArea>
              )}
            </>
          )}
        </CardContent>
      </Card>
      <NewConversationDialog open={newConversationOpen} onOpenChange={setNewConversationOpen} />
    </>
  );
}
